/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Typography, Typography2, typography2 } from '@coursera/cds-core';
import { type EventData } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import LearningObjective from 'bundles/unified-description-page-common/components/shared/LearningObjective';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import TogglableDescription, {
  getCollapsedHeight,
} from 'bundles/unified-description-page-common/components/shared/TogglableDescription';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import getSpecializationDescription from 'bundles/unified-description-page-common/utils/getSpecializationDescription';

import _t from 'i18n!nls/xdp';

type S12nFragments = EnterpriseSpecializationPageFieldsFragment | ConsumerSpecializationPageFieldsFragment;
type CourseFragments = EnterpriseCoursePageFieldsFragment | ConsumerCoursePageFieldsFragment;
type CombinedFragments = S12nFragments | CourseFragments;

/**
 * Common configurations for this component are:
 *
 * S12ns (Specializations, Professional Certificates) to show the
 * learning objectives and a fallback of description, or the new redesigns:
 *  - cmlDescription
 *  - projectsOverview
 *  - description
 *  - learningObjectives
 *  - title
 *
 * Courses (Courses, Projects, Guided Projects) to show only the list of
 * learning objectives
 *  - learningObjectives
 *  - title
 */
type Props = {
  /** Optional field only available to S12n* pages */
  cmlDescription?: S12nFragments['cmlDescription'];
  /** Optional field only available to S12n* pages */
  projectsOverview?: S12nFragments['projectsOverview'];
  /** Optional field, Courses don't always want to render description */
  description?: CombinedFragments['description'];
  learningObjectives: CombinedFragments['cmlLearningObjectives'];
  title?: string;
};

const styles = {
  listContainer: css`
    & > li {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  description: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  descriptionTitle: css`
    ${typography2.subtitleLarge};
    margin-bottom: var(--cds-spacing-50);
  `,
  descriptionTitleBestOfBreed: css`
    ${typography2.subtitleMedium};
    margin-bottom: var(--cds-spacing-50);
  `,
};

const LearningObjectives = ({ learningObjectives, title, cmlDescription, projectsOverview, description }: Props) => {
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework, enableXDPRedesignHero, enableXDPRedesignKIM } =
    useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;
  // We modify heading styling if the entire page is redesigned.
  const enableBestOfBreed = enableXDPRedesignDescriptionRework && enableXDPRedesignHero && enableXDPRedesignKIM;

  const eventingData: EventData<'expand_accordion' | 'collapse_accordion'> = {
    accordion: {
      name: 'description_page_overview',
      header: 'Description page summary overview read more',
      body: '',
    },
  };

  // To prevent redesigns to render without content.
  if (!learningObjectives?.length && !description) {
    return null;
  }

  const getTitle = () => {
    if (title) {
      return title;
    }

    if (enableXDPRedesignDescriptionRework) {
      return _t("What you'll learn");
    }

    if (enableXDPRedesignPhase1) {
      return _t('Overview');
    }

    return _t("What you'll learn");
  };

  const getObjectives = () => {
    if (enableXDPRedesign) {
      return (
        <Grid item container spacing={8} component="ul" css={styles.listContainer}>
          {learningObjectives?.map((objective) => {
            return (
              <Grid item xs={12} key={objective.cmlValue} component="li">
                <LearningObjective objective={objective} />
              </Grid>
            );
          })}
        </Grid>
      );
    }

    return (
      <Grid container spacing={16} component="ul">
        {learningObjectives?.map((objective) => {
          return (
            <Grid item xs={12} key={objective.cmlValue} component="li">
              <LearningObjective objective={objective} />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // The fallback for when there are no learning objectives, in this case return the description.
  // Example S12n: https://www.coursera.org/specializations/aws-fundamentals
  if (!learningObjectives?.length) {
    const collapsedHeight = getCollapsedHeight({
      useNarrowLayout: enableXDPRedesign,
      useExtendedHeight: true,
    });

    return (
      <Subsection title={<Typography variant="h2semibold">{title ?? _t("What you'll learn")}</Typography>} spacing={24}>
        <TrackedDiv
          trackingName="what_you_will_learn_section"
          withVisibilityTracking={true}
          requireFullyVisible={false}
          trackClicks={false}
        >
          <TogglableDescription
            description={description}
            cmlDescription={cmlDescription}
            collapsedHeight={collapsedHeight}
            enableGradient
            trackingData={eventingData}
          />
        </TrackedDiv>
      </Subsection>
    );
  }

  if (enableXDPRedesignDescriptionRework) {
    const s12nDescription = getSpecializationDescription(cmlDescription, undefined);

    const collapsedHeight = getCollapsedHeight({
      useNarrowLayout: enableXDPRedesign,
      useExtendedHeight: !learningObjectives?.length,
    });

    const titleHeadingVariant = enableBestOfBreed ? 'subtitleMedium' : 'subtitleLarge';

    return (
      <Subsection
        title={
          <Typography2 component="h2" variant={titleHeadingVariant}>
            {getTitle()}
          </Typography2>
        }
        spacing={18}
      >
        <TrackedDiv
          trackingName="what_you_will_learn_section"
          withVisibilityTracking={true}
          requireFullyVisible={false}
          trackClicks={false}
        >
          {getObjectives()}
          <Typography2
            component="h2"
            variant={titleHeadingVariant}
            css={enableBestOfBreed ? styles.descriptionTitleBestOfBreed : styles.descriptionTitle}
          >
            {_t('Overview')}
          </Typography2>
          <Typography2 component="div" variant="bodyPrimary" css={styles.description}>
            <TogglableDescription
              description={description}
              cmlDescription={s12nDescription}
              trackingName="about_description"
              collapsedHeight={collapsedHeight}
              enableGradient
              trackingData={eventingData}
            />
          </Typography2>
        </TrackedDiv>
      </Subsection>
    );
  }

  if (enableXDPRedesignPhase1) {
    const descriptionWithProjectOverview = getSpecializationDescription(cmlDescription, projectsOverview);

    const collapsedHeight = getCollapsedHeight({
      useNarrowLayout: enableXDPRedesign,
      useExtendedHeight: !learningObjectives?.length,
    });

    return (
      <Subsection
        title={
          <Typography2 component="h2" variant="subtitleLarge">
            {getTitle()}
          </Typography2>
        }
        spacing={18}
      >
        <TrackedDiv
          trackingName="what_you_will_learn_section"
          withVisibilityTracking={true}
          requireFullyVisible={false}
          trackClicks={false}
        >
          <Typography2 component="div" variant="bodyPrimary" css={styles.description}>
            <TogglableDescription
              description={description}
              cmlDescription={descriptionWithProjectOverview}
              trackingName="about_description"
              collapsedHeight={collapsedHeight}
              enableGradient
              trackingData={eventingData}
            />
          </Typography2>
          {getObjectives()}
        </TrackedDiv>
      </Subsection>
    );
  }

  return (
    <Subsection title={<Typography variant="h2semibold">{getTitle()}</Typography>} spacing={24}>
      <TrackedDiv
        trackingName="what_you_will_learn_section"
        withVisibilityTracking={true}
        requireFullyVisible={false}
        trackClicks={false}
      >
        <Grid container spacing={16} component="ul">
          {learningObjectives.map((objective) => {
            return (
              <Grid item xs={12} sm={6} key={objective.cmlValue} component="li">
                <LearningObjective objective={objective} />
              </Grid>
            );
          })}
        </Grid>
      </TrackedDiv>
    </Subsection>
  );
};

export default LearningObjectives;
